import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { Spinner } from "src/core/components/custom-ui/spinner";
import { cn } from "src/core/lib/utils";

const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-x-10 whitespace-nowrap rounded-button text-base font-light",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0",
    "disabled:cursor-not-allowed",
  ].join(" "),
  {
    variants: {
      variant: {
        default: [
          "bg-primary text-primary-foreground border border-transparent",
          "hover:border hover:border-primary hover:text-primary hover:bg-primary/10",
          "disabled:bg-primary/30 disabled:opacity-50",
        ].join(" "),
        destructive: [
          "bg-destructive text-destructive-foreground",
          "hover:bg-destructive/90",
          "disabled:opacity-50",
        ].join(" "),
        outline: [
          "bg-white text-primary justify-start border border-border-secondary",
          "hover:bg-accent hover:text-accent-foreground hover:text-primary ",
          "disabled:text-disabled disabled:opacity-50",
        ].join(" "),
        outlinePrimary: [
          "bg-white text-primary justify-start border border-primary",
          "hover:bg-accent hover:text-accent-foreground hover:text-primary ",
          "disabled:text-disabled disabled:opacity-50",
        ].join(" "),
        secondary: [
          "bg-popover text-secondary-foreground border border-transparent",
          "hover:border hover:border-secondary-foreground",
          "disabled:opacity-50",
        ].join(" "),
        secondaryDestructive: [
          "bg-popover text-red-medium border border-transparent",
          "hover:border hover:border-red-medium",
          "disabled:opacity-50",
        ].join(" "),
        ghost: "hover:bg-accent hover:text-accent-foreground disabled:opacity-50",
        link: [
          "bg-gray-xlight text-primary border border-transparent",
          "hover:border hover:border-primary",
          "disabled:opacity-50",
        ].join(" "),
        tertiary: [
          "bg-orange-medium text-gray-xlight border border-transparent",
          "hover:border hover:border-orange-medium hover:bg-orange-hover hover:text-orange-medium",
          "disabled:opacity-50",
        ].join(" "),
        tertiaryWhite: [
          "bg-gray-xlight text-orange-medium border border-transparent",
          "hover:border hover:border-orange-medium disabled:opacity-50",
        ].join(" "),
        combobox: [
          "bg-white text-base justify-start border border-input",
          "disabled:text-disabled disabled:border-disabled",
        ].join(" "),
        icon: [
          "p-0 bg-primary text-primary-foreground border border-transparent rounded-full",
          "hover:border-primary hover:text-primary hover:bg-primary/10 hover:border",
          "disabled:bg-primary/30 disabled:opacity-50",
        ].join(" "),

        action: ["text-muted hover:text-foreground disabled:opacity-50"].join(" "),
        actionWithText: ["text-primary hover:text-foreground gap-x-10", "disabled:opacity-50"].join(
          " "
        ),
        cancel: [
          "bg-red-medium text-primary-foreground border border-transparent ",
          "hover:border hover:bg-red-medium/10 hover:border-red-medium hover:text-red-medium",
          "disabled:opacity-50",
        ].join(" "),
        alertLink: ["text-primary disabled:opacity-50"].join(" "),
        iconAction: ["text-foreground bg-dialog rounded-lg border border-border"].join(" "),
        linkDestructive: [
          "flex flex-row h-full items-center gap-x-10 text-foreground hover:underline hover:cursor-pointer",
        ].join(" "),
        linkWhite: [
          "flex flex-row h-full items-center gap-x-10 text-gray-xxlight hover:underline hover:cursor-pointer",
        ].join(" "),
        linkMuted: ["flex items-center gap-[5px] cursor-pointer hover:underline"].join(" "),
        iconDestructive: [
          "justify-center text-muted-foreground hover:text-foreground hover:cursor-pointer",
        ].join(" "),
        sideTab: [
          "flex flex-row items-center justify-center gap-5 bg-dialog rounded-none rounded-l-lg p-5 group w-[180px] h-40 absolute top-15 left-[-180px]",
        ].join(" "),
        sideTabMuted: [
          "group flex flex-row items-center justify-center gap-5 bg-border-secondary rounded-none rounded-l-lg p-5 w-[140px] h-40",
        ].join(" "),
        selectCard: [
          "w-full block person-card items-stretch text-center data-[selected=true]:border-foreground data-[selected=true]:text-foreground",
        ].join(" "),
        card: [
          "w-full rounded-lg border border-foreground hover:cursor-pointer hover:bg-blue-hover hover:border-primary",
        ].join(" "),
        upload: [
          "bg-white text-foreground justify-start border border-dashed border-input",
          "hover:bg-blue-hover hover:border-tabs-border hover:text-blue-completed",
          "disabled:text-disabled disabled:opacity-50",
        ].join(" "),
        none: [].join(" "),
        simpleLink: [
          "flex flex-row h-full items-center gap-x-10 hover:underline hover:cursor-pointer justify-start",
        ].join(" "),
        iconTertiary: [
          "text-orange-medium border border-orange-medium rounded-lg px-10 text-sm h-30",
          "hover:bg-orange-xlight",
          "disabled:opacity-50 disabled:text-foreground/50 disabled:border-foreground/10 disabled:cursor-not-allowed",
        ].join(" "),
        button4: [
          "flex flex-row w-full h-30 items-center justify-center gap-x-5 font-light text-foreground/35 rounded-lg",
          "border border-foreground/10",
          "hover:border-blue-info hover:text-blue-info",
          "data-[selected=true]:border-blue-info data-[selected=true]:text-blue-info",
        ].join(" "),
      },
      size: {
        default: "h-button px-15",
        small: "h-buttonSmall p-10",
        none: "",
        card: "h-60 px-15",
        icon: "h-30 w-30",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export type ButtonProps = {
  asChild?: boolean;
  loading?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants>;

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, loading = false, children, disabled, ...props },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {children}
        {loading && <Spinner size="sm" />}
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
